import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { useApi } from "@platform/api";

import { downloadBinaryFile } from "../../utils";

import type { StewardshipDateBasis } from "./useBrokerStewardship";
import type { AxiosInstance } from "axios";

export type StewardshipPowerPointDownloadParams = {
  brokerId: string;
  startDate?: string;
  endDate?: string;
  dateBasis?: StewardshipDateBasis;
};

const FALLBACK_FILENAME = "Stewardship Report.pptx";

function filenameFromContentDisposition(header: string | undefined): string {
  if (!header) {
    return FALLBACK_FILENAME;
  }

  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      // Fall through to the plain filename parameter.
    }
  }

  const quoted = /filename="([^"]+)"/i.exec(header);
  return quoted?.[1] ?? FALLBACK_FILENAME;
}

async function requestStewardshipPowerPoint(
  axiosInstance: AxiosInstance,
  params: StewardshipPowerPointDownloadParams,
): Promise<string> {
  const response = await axiosInstance.get(
    `/consulting/v1/brokers/${params.brokerId}/stewardship/powerpoint`,
    {
      params: {
        ...(params.startDate ? { start_date: params.startDate } : {}),
        ...(params.endDate ? { end_date: params.endDate } : {}),
        // Omit for the submitted default so existing URLs/caches are unchanged.
        ...(params.dateBasis === "effective"
          ? { date_basis: "effective" }
          : {}),
      },
      responseType: "blob",
    },
  );

  const fileName = filenameFromContentDisposition(
    response.headers["content-disposition"] as string | undefined,
  );

  downloadBinaryFile(response.data as Blob, fileName);

  return fileName;
}

export function useStewardshipPowerPointDownload(): UseMutationResult<
  string,
  Error,
  StewardshipPowerPointDownloadParams
> {
  const axiosInstance = useApi();

  return useMutation<string, Error, StewardshipPowerPointDownloadParams>({
    mutationFn: (params) => requestStewardshipPowerPoint(axiosInstance, params),
  });
}
